<script setup lang="ts">
/**
 * A field that records a key combination: it reads like a select showing the
 * stored combo as keycaps, and while it records („Kürzel drücken …“) the next
 * press that is not a lone modifier becomes the value. The combo is written in
 * the kit's notation (`'Mod+Shift+F'`, `''` = none): ⌘ is `Mod` on an Apple
 * platform and Ctrl elsewhere, see {@link comboFromKeyEvent}.
 *
 * A click, Enter or Space starts recording — focusing the field does not, so
 * Tab, the arrow keys and every other key behave normally while it is idle
 * (`autofocus` only takes the focus when the field appears). While it records,
 * every key press stays inside the field (`preventDefault` +
 * `stopPropagation`) — the press must not reach a command palette or the
 * page; a key the notation has no name for (a dead key, `ContextMenu` …) is
 * swallowed and recording goes on. Escape, Backspace and Delete without a
 * modifier end recording without a value (`cancel`; „none“ is a choice of its
 * own, not a key here), and so does leaving the field. A plain Tab leaves
 * the field untouched, so it is no keyboard trap. Escape on the idle field
 * is a `cancel`, too, and so is leaving it.
 *
 * The field is named by its `<label for>` (or `ariaLabel`); what it shows —
 * the stored combo, „Kürzel aufnehmen“, „Kürzel drücken …“ — is its
 * description, and a polite live region says that recording started.
 *
 * `validate` refuses combos: its message shows under the field, nothing is
 * emitted and the field keeps recording.
 */
import { computed, inject, nextTick, onMounted, ref, useId, useTemplateRef } from 'vue';
import { i18nInjectionKey } from '../internal';
import NxKbd from './NxKbd.vue';
import { comboFromKeyEvent, isApplePlatform } from './keys';

const props = withDefaults(
  defineProps<{
    /** Metrics of the field, the same as `NxNativeSelect`'s. */
    size?: 'compact' | 'field' | 'full';
    disabled?: boolean;
    id?: string;
    /** Accessible name when no `<label for>` names the field. */
    ariaLabel?: string;
    /** A message for a refused combo, null when it may be taken. */
    validate?: (combo: string) => string | null;
    /** Take the focus when the field appears (it stays idle until clicked, Entered or Spaced). */
    autofocus?: boolean;
    /** Force the Apple (⌘) or the other (Strg) notation. */
    apple?: boolean;
  }>(),
  {
    size: 'field',
    disabled: false,
    id: undefined,
    ariaLabel: undefined,
    validate: undefined,
    autofocus: false,
    apple: undefined,
  }
);

const model = defineModel<string>({ default: '' });

const emit = defineEmits<{
  /** Recording ended without a value (Escape, Backspace, Delete), or Escape or the focus left the idle field. */
  cancel: [];
}>();

const i18n = inject(i18nInjectionKey, null);

const FALLBACK_TEXTS: Record<string, string> = {
  press: 'Kürzel drücken …',
  cancel: 'Abbrechen',
  record: 'Kürzel aufnehmen',
};

function text(key: string): string {
  return i18n ? i18n.t(`common.keybinding_input.${key}`) : FALLBACK_TEXTS[key]!;
}

const button = useTemplateRef<HTMLButtonElement>('button');
const generatedId = useId();
const recording = ref(false);
const error = ref<string | null>(null);
const fieldId = computed(() => props.id ?? generatedId);
const errorId = computed(() => `${fieldId.value}-error`);
const contentId = computed(() => `${fieldId.value}-content`);
const describedBy = computed(() =>
  error.value ? `${contentId.value} ${errorId.value}` : contentId.value
);

const apple = computed(() => props.apple ?? isApplePlatform());

function start(): void {
  if (props.disabled) return;
  recording.value = true;
  // Safari does not focus a button on click; the keys need to land here.
  button.value?.focus();
}

/** Leave recording; `cancel` when it ended without a value. */
function stop(cancelled: boolean): void {
  const wasRecording = recording.value;
  recording.value = false;
  error.value = null;
  if (wasRecording && cancelled) emit('cancel');
}

function onKeydown(event: KeyboardEvent): void {
  if (props.disabled) return;
  if (!recording.value) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      start();
    } else if (event.key === 'Escape') {
      emit('cancel');
    }
    return;
  }
  const plain = !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey;
  // Tab walks on: a field that swallowed it would be a keyboard trap.
  if (event.key === 'Tab' && !event.ctrlKey && !event.metaKey && !event.altKey) return;
  event.preventDefault();
  event.stopPropagation();
  if (plain && (event.key === 'Escape' || event.key === 'Backspace' || event.key === 'Delete')) {
    stop(true);
    return;
  }
  const combo = comboFromKeyEvent(event, apple.value);
  if (combo === null) return;
  const problem = props.validate?.(combo) ?? null;
  if (problem) {
    error.value = problem;
    return;
  }
  error.value = null;
  recording.value = false;
  model.value = combo;
}

function onBlur(): void {
  if (recording.value) stop(true);
  else emit('cancel');
}

onMounted(async () => {
  if (!props.autofocus || props.disabled) return;
  await nextTick();
  button.value?.focus();
});
</script>

<template>
  <span class="nx-keybinding-input" :class="`size-${size}`">
    <button
      :id="id"
      ref="button"
      type="button"
      class="nx-keybinding-input__field"
      :class="{ 'is-recording': recording, 'is-invalid': !!error }"
      :disabled="disabled"
      :aria-label="ariaLabel"
      :aria-invalid="error ? 'true' : undefined"
      :aria-describedby="describedBy"
      data-testid="keybinding-input"
      @click="start"
      @blur="onBlur"
      @keydown="onKeydown"
    >
      <span :id="contentId" class="nx-keybinding-input__content">
        <template v-if="recording">
          <span class="nx-keybinding-input__prompt">{{ text('press') }}</span>
          <span class="nx-keybinding-input__hint" :title="text('cancel')">
            <NxKbd keys="Escape" size="sm" :apple="apple" />
          </span>
        </template>
        <NxKbd v-else-if="model" :keys="model" size="sm" :apple="apple" />
        <span v-else class="nx-keybinding-input__prompt">{{ text('record') }}</span>
      </span>
    </button>
    <span class="nx-keybinding-input__status" aria-live="polite">
      {{ recording ? text('press') : '' }}
    </span>
    <p v-if="error" :id="errorId" class="nx-keybinding-input__error" role="alert">{{ error }}</p>
  </span>
</template>

<style scoped>
.nx-keybinding-input {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}

/* The look of an NxNativeSelect: same fill, border, focus ring and metrics. */
.nx-keybinding-input__field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--nxt-space-sm);
  box-sizing: border-box;
  width: 100%;
  margin: 0;
  font-family: var(--nxt-font);
  font-weight: var(--nxt-font-weight-medium);
  text-align: start;
  color: var(--nxt-select-fg);
  background: var(--nxt-input-bg);
  border: var(--nxt-hairline) solid var(--nxt-input-border);
  border-radius: var(--nxt-radius-sm);
  outline: none;
  cursor: pointer;
  transition:
    border-color 0.15s,
    background-color 0.15s,
    box-shadow 0.15s;

  &:hover:not(:focus):not(:disabled) {
    border-color: var(--nxt-border);
  }

  &:focus,
  &.is-recording {
    border-color: var(--nxt-select-border-focus);
    box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring-soft);
  }

  &.is-invalid {
    border-color: var(--nxt-danger-border);
  }

  &:disabled {
    cursor: not-allowed;
    background: var(--nxt-select-bg-disabled);
    color: var(--nxt-input-fg-disabled);
  }
}

.size-compact .nx-keybinding-input__field {
  font-size: var(--nxt-font-size-xs);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
}
.size-compact {
  max-width: calc(var(--nxt-size-unit) * 8.125);
}

.size-field .nx-keybinding-input__field {
  min-height: var(--nxt-control-size-md);
  font-size: var(--nxt-font-size-sm);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
}
.size-field {
  max-width: var(--nxt-panel-width-sm);
}

.size-full {
  display: flex;
  width: 100%;
}
.size-full .nx-keybinding-input__field {
  min-height: var(--nxt-control-size-md);
  font-size: var(--nxt-font-size-sm);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
}

.nx-keybinding-input__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--nxt-space-sm);
  flex: 1 1 auto;
  min-width: 0;
}

/* Visually hidden, read out: that recording has started. */
.nx-keybinding-input__status {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  padding: 0;
  margin: calc(var(--nxt-hairline) * -1);
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.nx-keybinding-input__prompt {
  color: var(--nxt-fg-muted);
}

.nx-keybinding-input__hint {
  display: inline-flex;
  flex: 0 0 auto;
}

.nx-keybinding-input__error {
  margin: 0;
  color: var(--nxt-danger-subtle-fg);
  font-size: var(--nxt-font-size-xs);
  overflow-wrap: anywhere;
}
</style>
